import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { WebContainer } from '@webcontainer/api';
import { toastError, toastWarning } from '@ideoz/ui';
import type { HostToIframeEditMessage } from '../preview-bridge/protocol';
import { isIdeozEditMessage } from '../preview-bridge/protocol';
import {
  persistJsxAttributeLiteral,
  persistMergedClassName,
  persistSelectOptions,
  persistTextEdit,
} from '../preview-bridge/applyVisualEdit';
import { mergeTailwindFromCssEdit } from '../preview-bridge/tailwindPatch';
import { selectFile } from '../stores/editor';
import type { HotEditBridgeApi, HotEditSelection } from '../stores/hotEdit';
import { EDIT_MARKS, forgetTakenAsk, holdEditModeThroughReload, hotEditStore } from '../stores/hotEdit';
import {
  dropLast,
  pendingEdits,
  queueEdit,
  touchedElements,
  type PendingEdit,
  type PendingEditInput,
} from '../preview-bridge/pendingEdits';
import { applyEdits } from '../preview-bridge/applyPendingEdits';
import { requestLeaveEditMode } from '../components/hot-edit/LeavePendingPrompt';
import { elementLabel } from '../components/hot-edit/inspectorModel';
import type { HotEditElementKind } from '../preview-bridge/elementKind';
import { getEditableSections } from '../preview-bridge/elementKind';
import { workbenchStore } from '../stores/workbench';
import { ensureHotEditBridgeFiles } from '../utils/ensureHotEditBridge';
import { recordWorkbenchMemory } from '../utils/workbenchMemoryTelemetry';
import { HOT_EDIT_ASK_IDEOZ_EVENT, HOT_EDIT_DESIGN_SYSTEM_EVENT } from '../utils/workbenchEvents';
import { designSystemPatchFor } from '../preview-bridge/designSystemSync';
import { elementContextOf } from '../preview-bridge/elementContext';
import { assetPathForDataUri, parseDataUri } from '../preview-bridge/imageReplace';

const DEBOUNCE_MS = 420;

/** Optional analytics bridge — host app may listen on `window` for `ideoz-hot-edit-analytics`. */
function emitHotEditAnalytics(eventName: string, properties?: Record<string, unknown>): void {
  try {
    window.dispatchEvent(new CustomEvent('ideoz-hot-edit-analytics', { detail: { eventName, properties } }));
  } catch {
    /* noop */
  }
}

/** False when iframe omits flags (legacy v1 bridges). */
function selectionCanPersistClasses(sel: HotEditSelection | null): boolean {
  if (!sel?.source?.filePath) return false;
  return sel.canPersistClasses !== false;
}

function parseEditMark(raw: unknown): HotEditSelection['editMark'] {
  return typeof raw === 'string' && (EDIT_MARKS as readonly string[]).includes(raw)
    ? (raw as NonNullable<HotEditSelection['editMark']>)
    : null;
}

function parseListRow(raw: unknown): HotEditSelection['listRow'] {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const index = Number(o.index);
  const count = Number(o.count);
  if (!Number.isFinite(index) || !Number.isFinite(count) || index <= 0 || count < 2) return null;
  return {
    key: typeof o.key === 'string' && o.key ? o.key : null,
    index,
    count,
    isRowRoot: o.isRowRoot === true,
  };
}

function parseListContainer(raw: unknown): HotEditSelection['listContainer'] {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const count = Number(o.count);
  if (!Number.isFinite(count) || count < 2) return null;
  return { count, firstKey: typeof o.firstKey === 'string' && o.firstKey ? o.firstKey : null };
}

function parseBreadcrumb(raw: unknown): HotEditSelection['breadcrumb'] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({ label: String(c.label ?? ''), elementId: String(c.elementId ?? '') }))
    .filter((c) => c.label && c.elementId);
}

function parseThemeTokens(raw: unknown): HotEditSelection['themeTokens'] {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  }
  return out;
}

/** Stands in for the WebContainer when the bridge was set up for the light preview. */

/** The class Lucide writes for a component name: `ShoppingCart` draws `lucide-shopping-cart`. */
function kebabIconName(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/** How often, and how many times, to ask a loaded preview whether its bridge is there. */
const BRIDGE_PING_MS = 500;
const BRIDGE_PING_TRIES = 12;
/** How long entering edit mode waits for a reloaded preview before going ahead anyway. */
const BRIDGE_HELLO_WAIT_MS = 8000;

export function useHotEditBridge(opts: {
  previewUrl: string;
  iframeLoaded: boolean;
}): {
  previewIframeRef: RefObject<HTMLIFrameElement | null>;
  bridgeReady: boolean;
  toggleHotEditMode: () => Promise<void>;
  applyInstantCss: (elementId: string, property: string, value: string) => void;
  applyInstantText: (elementId: string, text: string) => void;
  scheduleApplyInstantText: (elementId: string, text: string) => void;
  applyInstantDom: (elementId: string, key: string, value: string) => void;
  applyInstantData: (
    rowKey: string,
    oldValue: string,
    newValue: string,
    cellIndex?: number,
  ) => void;
  applyInstantRow: (row: {
    op: 'add' | 'duplicate' | 'remove' | 'move';
    rowKey: string;
    newKey?: string;
    toIndex?: number;
    values?: { from: string; to: string }[];
  }) => void;
  queuePersistTypographyLayout: () => void;
  queuePersistText: (text: string) => void;
  queuePersistPlaceholder: (placeholder: string) => void;
  queuePersistDomValue: (value: string) => void;
  queuePersistSelectOptions: (options: string[]) => void;
  queuePersistInputType: (type: string) => void;
  bumpMergedFromTailwind: (property: string, value: string) => void;
  /** Hide or show the selected element, in the preview and in the change waiting to be saved. */
  toggleHidden: (hide: boolean) => void;
  mergedClassDraftRef: MutableRefObject<string>;
  cnDeltaMergedRef: MutableRefObject<string>;
  exitHotEditMode: () => void;
  openSelectionInEditor: () => void;
} {
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [bridgeReady, setBridgeReady] = useState(false);
  const mergedClassDraftRef = useRef('');
  /** Snapshot requests waiting on the preview, by request id (step 5's Ask Ideoz chip). */
  const snapshotWaitersRef = useRef(new Map<string, (dataUrl: string | null) => void>());
  /** Callers waiting for a reloaded preview to say hello again. */
  const bridgeHelloWaitersRef = useRef<Array<() => void>>([]);
  /** Extra utilities appended into `cn()` when persisting component usage sites */
  const cnDeltaMergedRef = useRef('');
  const persistTimerRef = useRef<number | null>(null);
  const textPersistTimerRef = useRef<number | null>(null);
  const pendingTextRef = useRef<{ selection: HotEditSelection; text: string } | null>(null);
  const placeholderPersistTimerRef = useRef<number | null>(null);
  const pendingPlaceholderRef = useRef<{ selection: HotEditSelection; placeholder: string } | null>(
    null,
  );
  const valuePersistTimerRef = useRef<number | null>(null);
  const pendingValueRef = useRef<{ selection: HotEditSelection; value: string } | null>(null);
  const selectOptionsPersistTimerRef = useRef<number | null>(null);
  const pendingSelectOptionsRef = useRef<{ selection: HotEditSelection; options: string[] } | null>(
    null,
  );
  const inputTypePersistTimerRef = useRef<number | null>(null);
  const pendingInputTypeRef = useRef<{ selection: HotEditSelection; type: string } | null>(null);

  type TypographyPersistJob = { selection: HotEditSelection; cnAppend: string };
  const pendingTypographyRef = useRef<TypographyPersistJob | null>(null);

  const textInstantRafRef = useRef<number | null>(null);
  const flushAllPendingRef = useRef<(() => Promise<void>) | null>(null);
  const pendingInstantTextRef = useRef<{ elementId: string; text: string } | null>(null);

  /** The last change that was saved, so a failure can be tried again from the panel. */
  const lastSaveRef = useRef<(() => Promise<void>) | null>(null);
  const markSaving = useCallback(() => hotEditStore.save.set('saving'), []);
  const markSaved = useCallback(() => {
    hotEditStore.save.set('saved');
    window.setTimeout(() => {
      if (hotEditStore.save.get() === 'saved') hotEditStore.save.set('idle');
    }, 3000);
  }, []);
  const markFailed = useCallback((retry: () => Promise<void>) => {
    lastSaveRef.current = retry;
    hotEditStore.save.set('failed');
  }, []);

  const postToIframe = useCallback(
    (msg: HostToIframeEditMessage) => {
      const win = previewIframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(msg, '*');
    },
    [],
  );

  const syncEditModeToIframe = useCallback(() => {
    postToIframe({ type: 'IDEOZ_EDIT_ENABLE', enabled: hotEditStore.mode.get() });
  }, [postToIframe]);

  const openSelectionInEditor = useCallback(() => {
    const sel = hotEditStore.selection.get();
    const path = sel?.source?.filePath;
    if (!path) return;
    selectFile(path);
    window.dispatchEvent(new CustomEvent('ideoz-workbench-open-code'));
  }, []);

  useEffect(() => {
    syncEditModeToIframe();
    const unsub = hotEditStore.mode.subscribe(() => {
      syncEditModeToIframe();
    });
    return unsub;
  }, [syncEditModeToIframe]);

  useEffect(() => {
    if (!opts.iframeLoaded) return;
    syncEditModeToIframe();
  }, [opts.iframeLoaded, syncEditModeToIframe]);

  /** Pointer left preview iframe (e.g. toward Hot edit panel) — iframe stops getting pointermove; clear stale hover box. */
  useEffect(() => {
    if (!opts.iframeLoaded) return;

    const attach = (): (() => void) | undefined => {
      const iframe = previewIframeRef.current;
      if (!iframe || !hotEditStore.mode.get()) return undefined;

      const clearHover = () => {
        postToIframe({ type: 'IDEOZ_EDIT_CLEAR_HOVER' });
      };

      iframe.addEventListener('mouseleave', clearHover);
      iframe.addEventListener('pointerleave', clearHover);
      return () => {
        iframe.removeEventListener('mouseleave', clearHover);
        iframe.removeEventListener('pointerleave', clearHover);
      };
    };

    let detach = attach();
    const unsubMode = hotEditStore.mode.subscribe(() => {
      detach?.();
      detach = attach();
    });

    return () => {
      unsubMode();
      detach?.();
    };
  }, [opts.iframeLoaded, postToIframe]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (!isIdeozEditMessage(ev.data)) return;
      const iframeWin = previewIframeRef.current?.contentWindow;
      if (iframeWin && ev.source !== iframeWin) return;
      const msg = ev.data as Record<string, unknown>;
      switch (msg.type) {
        case 'IDEOZ_EDIT_READY': {
          setBridgeReady(true);
          bridgeHelloWaitersRef.current.splice(0).forEach((done) => done());
          hotEditStore.iframePathname.set(String(msg.pathname ?? '/'));
          syncEditModeToIframe();
          // The light preview loads a new page after every save: select the same element again,
          // under the same id, so the inspector stays on it.
          const sel = hotEditStore.selection.get();
          if (hotEditStore.mode.get() && sel?.selectorPath) {
            postToIframe({ type: 'IDEOZ_EDIT_RESELECT', selectorPath: sel.selectorPath, elementId: sel.elementId });
          }
          break;
        }
        // The bridge announces itself once. If that one message lands while the parent is still
        // holding the old iframe - which is exactly what happens on the reload after Apply - the
        // announcement is gone for good and Edit stays greyed out on "Connecting preview inspect
        // bridge...". So we also ask, and an answer counts.
        case 'IDEOZ_EDIT_PONG': {
          setBridgeReady(true);
          bridgeHelloWaitersRef.current.splice(0).forEach((done) => done());
          syncEditModeToIframe();
          break;
        }
        case 'IDEOZ_EDIT_SELECT': {

          const kindRaw = msg.elementKind;
          const ek = typeof kindRaw === 'string' ? (kindRaw as HotEditElementKind) : undefined;
          const elementKindResolved: HotEditElementKind = ek ?? 'container';

          mergedClassDraftRef.current = String(msg.className ?? '');
          cnDeltaMergedRef.current = '';

          const listCtx: HotEditSelection['listContext'] = ((): HotEditSelection['listContext'] => {
            const raw = msg.listContext;
            if (raw == null || typeof raw !== 'object') return null;
            const o = raw as Record<string, unknown>;
            return {
              inMapLoop: o.inMapLoop === true,
              mapRowIndex:
                typeof o.mapRowIndex === 'number'
                  ? o.mapRowIndex
                  : o.mapRowIndex != null && String(o.mapRowIndex).length
                    ? Number(o.mapRowIndex)
                    : null,
              reactKey: typeof o.reactKey === 'string' ? o.reactKey : o.reactKey == null ? null : String(o.reactKey),
              domNthOfType:
                typeof o.domNthOfType === 'number'
                  ? o.domNthOfType
                  : o.domNthOfType != null && String(o.domNthOfType).length
                    ? Number(o.domNthOfType)
                    : null,
              selectorPath:
                typeof o.selectorPath === 'string' ? o.selectorPath : undefined,
            };
          })();

          const hasListBinding = listCtx?.inMapLoop === true;

          const baseSections = getEditableSections(elementKindResolved);
          const editableSections =
            hasListBinding && !baseSections.includes('content')
              ? ['content' as const, ...baseSections]
              : baseSections;

          const rect = msg.rect as { top: number; left: number; width: number; height: number } | undefined;
          hotEditStore.selectionRect.set(rect ?? null);
          hotEditStore.selection.set({
            elementId: String(msg.elementId ?? ''),
            tagName: String(msg.tagName ?? ''),
            ownerComponentName:
              typeof msg.ownerComponentName === 'string' ? msg.ownerComponentName : undefined,
            source: (msg.source ?? null) as HotEditSelection['source'],
            implementationSource:
              (msg.implementationSource as HotEditSelection['implementationSource']) ?? undefined,
            selectorPath: typeof msg.selectorPath === 'string' ? msg.selectorPath : undefined,
            listContext: listCtx,
            computedStyles: (msg.computedStyles ?? {}) as HotEditSelection['computedStyles'],
            textContent: String(msg.textContent ?? ''),
            className: String(msg.className ?? ''),
            hasChildElements: Boolean(msg.hasChildElements),
            elementKind: ek,
            editTargetKind: msg.editTargetKind as HotEditSelection['editTargetKind'],
            canPersistClasses:
              typeof msg.canPersistClasses === 'boolean'
                ? Boolean(msg.canPersistClasses)
                : Boolean(msg.source),
            blockReason: msg.blockReason == null ? null : String(msg.blockReason),
            inputPlaceholder:
              typeof msg.inputPlaceholder === 'string' ? msg.inputPlaceholder : undefined,
            inputValue: typeof msg.inputValue === 'string' ? msg.inputValue : undefined,
            inputType: typeof msg.inputType === 'string' ? msg.inputType : undefined,
            optionValues: Array.isArray(msg.optionValues)
              ? (msg.optionValues as unknown[]).map((v) => String(v))
              : undefined,
            imageSrc: typeof msg.imageSrc === 'string' ? msg.imageSrc : undefined,
            isHidden: msg.isHidden === true,
            listRow: parseListRow(msg.listRow),
            listContainer: parseListContainer(msg.listContainer),
            editMark: parseEditMark(msg.editMark),
            hasEditMarks: msg.hasEditMarks === true,
            isShell: msg.isShell === true,
            childCount: typeof msg.childCount === 'number' ? msg.childCount : 0,
            breadcrumb: parseBreadcrumb(msg.breadcrumb),
            themeTokens: parseThemeTokens(msg.themeTokens),
            editableSections,
          });
          break;
        }
        case 'IDEOZ_EDIT_SNAPSHOT_RESULT': {
          const waiting = snapshotWaitersRef.current.get(String(msg.requestId ?? ''));
          if (waiting) {
            snapshotWaitersRef.current.delete(String(msg.requestId ?? ''));
            waiting(typeof msg.dataUrl === 'string' ? msg.dataUrl : null);
          }
          break;
        }
        case 'IDEOZ_EDIT_TEXT_INPUT': {
          // Typed in the preview: the same pending change the panel's text box makes.
          const text = typeof msg.text === 'string' ? msg.text : null;
          if (text != null && hotEditStore.selection.get()?.elementId === msg.elementId) {
            queuePersistTextRef.current?.(text);
          }
          break;
        }
        case 'IDEOZ_EDIT_RECT': {
          const r = msg.rect as { top: number; left: number; width: number; height: number } | undefined;
          if (r && hotEditStore.selection.get()?.elementId === msg.elementId) hotEditStore.selectionRect.set(r);
          break;
        }
        case 'IDEOZ_EDIT_DESELECT':
          hotEditStore.selection.set(null);
          hotEditStore.selectionRect.set(null);
          mergedClassDraftRef.current = '';
          cnDeltaMergedRef.current = '';
          break;
        case 'IDEOZ_EDIT_ROUTE':
          hotEditStore.iframePathname.set(String(msg.pathname ?? '/'));
          break;
        case 'IDEOZ_EDIT_HMR_IDLE': {
          const sel = hotEditStore.selection.get();
          if (!sel) break;
          postToIframe({ type: 'IDEOZ_EDIT_CLEAR_INLINE', elementId: sel.elementId });
          break;
        }
        case 'IDEOZ_EDIT_RENDERED':
          recordWorkbenchMemory('first-preview', { once: true });
          workbenchStore.previewCrash.set(null);
          workbenchStore.previewShowsTemplatePlaceholder.set(msg.templatePlaceholder === true);
          workbenchStore.previewRendered.set(true);
          break;
        case 'IDEOZ_EDIT_CRASHED':
          workbenchStore.previewRendered.set(false);
          workbenchStore.previewCrash.set({
            kind: msg.kind === 'compile' ? 'compile' : 'runtime',
            message: String(msg.message ?? 'Unknown error'),
            stack: typeof msg.stack === 'string' ? msg.stack : undefined,
            file: typeof msg.file === 'string' ? msg.file : undefined,
            pathname: String(msg.pathname ?? '/'),
            at: Date.now(),
          });
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [postToIframe, syncEditModeToIframe]);

  useEffect(() => {
    setBridgeReady(false);
    workbenchStore.previewRendered.set(false);
    workbenchStore.previewCrash.set(null);
    workbenchStore.previewShowsTemplatePlaceholder.set(false);
  }, [opts.previewUrl]);

  /** Ask the loaded preview whether it is there, until it says so. See `IDEOZ_EDIT_PONG` above. */
  useEffect(() => {
    if (!opts.iframeLoaded || bridgeReady) return undefined;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (tries > BRIDGE_PING_TRIES) {
        window.clearInterval(id);
        return;
      }
      postToIframe({ type: 'IDEOZ_EDIT_PING', requestId: `hello-${tries}` });
    }, BRIDGE_PING_MS);
    return () => window.clearInterval(id);
  }, [opts.iframeLoaded, bridgeReady, postToIframe]);

  /**
   * `iframeLoaded` turns false in the same commit that mounts a new iframe, before its scripts
   * can post anything, so this never clears a signal from the new page.
   */
  useEffect(() => {
    if (opts.iframeLoaded) return;
    setBridgeReady(false);
    workbenchStore.previewRendered.set(false);
    workbenchStore.previewCrash.set(null);
    workbenchStore.previewShowsTemplatePlaceholder.set(false);
  }, [opts.iframeLoaded]);

  const clearPersistTimers = useCallback(() => {
    if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    if (textPersistTimerRef.current) window.clearTimeout(textPersistTimerRef.current);
    if (placeholderPersistTimerRef.current) window.clearTimeout(placeholderPersistTimerRef.current);
    if (valuePersistTimerRef.current) window.clearTimeout(valuePersistTimerRef.current);
    if (selectOptionsPersistTimerRef.current) window.clearTimeout(selectOptionsPersistTimerRef.current);
    if (inputTypePersistTimerRef.current) window.clearTimeout(inputTypePersistTimerRef.current);
    persistTimerRef.current = null;
    textPersistTimerRef.current = null;
    placeholderPersistTimerRef.current = null;
    valuePersistTimerRef.current = null;
    selectOptionsPersistTimerRef.current = null;
    inputTypePersistTimerRef.current = null;
  }, []);

  const flushTypographyPersist = useCallback(async () => {
    persistTimerRef.current = null;
    const job = pendingTypographyRef.current;
    pendingTypographyRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const sel = job.selection;
    if (!selectionCanPersistClasses(sel)) return;

    const cnAppendOnly = job.cnAppend.trim();
    if (!cnAppendOnly) return;

    const r = await persistMergedClassName({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      cnAppendOnly,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
      componentName: sel.ownerComponentName,
      // All items: the class goes on the list's row template, shared by every row.
      listContext: hotEditStore.listScope.get() === 'all' ? null : sel.listContext,
    });

    if (!r.ok) {
      markFailed(async () => {
        pendingTypographyRef.current = job;
        await flushTypographyPersistRef.current?.();
      });
      toastWarning(r.error, '', 'Ask Ideoz', () => askIdeozAboutSelection(sel));
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    markSaved();
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'className', path: sel.source!.filePath });
  }, [openSelectionInEditor, markFailed, markSaved]);
  const flushTypographyPersistRef = useRef<(() => Promise<void>) | null>(null);
  flushTypographyPersistRef.current = flushTypographyPersist;

  const flushTextPersist = useCallback(async () => {
    textPersistTimerRef.current = null;
    const job = pendingTextRef.current;
    pendingTextRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, text } = job;
    if (!selectionCanPersistClasses(sel)) return;

    const r = await persistTextEdit({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
      componentName: sel.ownerComponentName,
      hintText: sel.textContent,
      listContext: sel.listContext,
      text,
    });

    if (!r.ok) {
      markFailed(async () => {
        pendingTextRef.current = job;
        await flushTextPersistRef.current?.();
      });
      toastError(r.error);
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    markSaved();
    emitHotEditAnalytics('hot_edit_patch_applied', {
      kind: 'text',
      path: r.writtenFilePath ?? sel.source!.filePath,
    });
  }, [markFailed, markSaved]);
  const flushTextPersistRef = useRef<(() => Promise<void>) | null>(null);
  flushTextPersistRef.current = flushTextPersist;

  const flushPlaceholderPersist = useCallback(async () => {
    placeholderPersistTimerRef.current = null;
    const job = pendingPlaceholderRef.current;
    pendingPlaceholderRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, placeholder } = job;
    if (!selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'placeholder',
      literalValue: placeholder,
    });

    if (!r.ok) {
      hotEditStore.save.set('failed');
      toastWarning(r.error, 'Placeholder stays inline in preview until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'placeholder', path: sel.source!.filePath });
  }, []);

  const flushValuePersist = useCallback(async () => {
    valuePersistTimerRef.current = null;
    const job = pendingValueRef.current;
    pendingValueRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, value: val } = job;
    if (!selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'defaultValue',
      literalValue: val,
    });

    if (!r.ok) {
      hotEditStore.save.set('failed');
      toastWarning(
        r.error,
        'Controlled inputs need Code edits — preview value stays inline.',
        'Open file',
        openSelectionInEditor,
      );
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'defaultValue', path: sel.source!.filePath });
  }, [openSelectionInEditor]);

  const flushSelectOptionsPersist = useCallback(async () => {
    selectOptionsPersistTimerRef.current = null;
    const job = pendingSelectOptionsRef.current;
    pendingSelectOptionsRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, options } = job;
    if (!selectionCanPersistClasses(sel)) return;

    const r = await persistSelectOptions({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      options,
      columnNumber: sel.source!.columnNumber,
      tagName: sel.tagName,
    });

    if (!r.ok) {
      hotEditStore.save.set('failed');
      toastWarning(r.error, 'Options stay inline in preview until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
      return;
    }
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'selectOptions', path: sel.source!.filePath });
  }, []);

  const flushInputTypePersist = useCallback(async () => {
    inputTypePersistTimerRef.current = null;
    const job = pendingInputTypeRef.current;
    pendingInputTypeRef.current = null;
    if (!job) return;

    const wc = workbenchStore.webcontainer.get() as WebContainer | null;
    const { selection: sel, type } = job;
    if (!selectionCanPersistClasses(sel)) return;

    const r = await persistJsxAttributeLiteral({
      wc,
      filePath: sel.source!.filePath,
      lineNumber: sel.source!.lineNumber,
      attributeName: 'type',
      literalValue: type,
    });

    if (!r.ok) {
      hotEditStore.save.set('failed');
      toastWarning(r.error, 'Input type stays inline until fixed in Code view.');
      emitHotEditAnalytics('hot_edit_save_failed', { reason: r.error });
    }
  }, []);

  const flushAllPending = useCallback(async () => {
    clearPersistTimers();
    await Promise.all([
      flushTypographyPersist(),
      flushTextPersist(),
      flushPlaceholderPersist(),
      flushValuePersist(),
      flushSelectOptionsPersist(),
      flushInputTypePersist(),
    ]);
  }, [
    clearPersistTimers,
    flushTypographyPersist,
    flushTextPersist,
    flushPlaceholderPersist,
    flushValuePersist,
    flushSelectOptionsPersist,
    flushInputTypePersist,
  ]);

  flushAllPendingRef.current = flushAllPending;

  /**
   * Every control queues its change instead of writing it (Prototype Editor UX plan, step 2).
   * The preview already shows it as an inline style or text; Apply writes them all in one pass.
   */
  const queue = useCallback((make: (sel: HotEditSelection) => PendingEditInput | null) => {
    const sel = hotEditStore.selection.get();
    if (!selectionCanPersistClasses(sel)) return;
    const edit = make(sel!);
    if (!edit) return;
    pendingEdits.set(queueEdit(pendingEdits.get(), edit));
    hotEditStore.save.set('pending');
  }, []);

  const queuePersistTypographyLayout = useCallback(() => {
    const delta = cnDeltaMergedRef.current.trim();
    if (!delta) return;
    queue((sel) => ({
      kind: 'class',
      elementId: sel.elementId,
      selection: sel,
      classDelta: delta,
      label: `Look of ${elementLabel(sel)}`,
    }));
  }, [queue]);

  const queuePersistTextRef = useRef<((text: string) => void) | null>(null);

  const queuePersistText = useCallback(
    (text: string) => {
      queue((sel) => ({
        kind: 'text',
        elementId: sel.elementId,
        selection: sel,
        text,
        label: `Text of ${elementLabel(sel)}`,
      }));
    },
    [queue],
  );

  queuePersistTextRef.current = queuePersistText;

  const queuePersistPlaceholder = useCallback(
    (placeholder: string) => {
      queue((sel) => ({
        kind: 'attr',
        elementId: sel.elementId,
        selection: sel,
        attribute: 'placeholder',
        value: placeholder,
        label: `Placeholder of ${elementLabel(sel)}`,
      }));
    },
    [queue],
  );

  const queuePersistDomValue = useCallback(
    (value: string) => {
      queue((sel) => ({
        kind: 'attr',
        elementId: sel.elementId,
        selection: sel,
        attribute: 'defaultValue',
        value,
        label: `Value of ${elementLabel(sel)}`,
      }));
    },
    [queue],
  );

  const queuePersistSelectOptions = useCallback(
    (options: string[]) => {
      queue((sel) => ({
        kind: 'options',
        elementId: sel.elementId,
        selection: sel,
        options,
        label: `Options of ${elementLabel(sel)}`,
      }));
    },
    [queue],
  );

  const queuePersistInputType = useCallback(
    (type: string) => {
      queue((sel) => ({
        kind: 'attr',
        elementId: sel.elementId,
        selection: sel,
        attribute: 'type',
        value: type,
        label: `Type of ${elementLabel(sel)}`,
      }));
    },
    [queue],
  );

  const bumpMergedFromTailwind = useCallback((property: string, value: string) => {
    mergedClassDraftRef.current = mergeTailwindFromCssEdit(
      mergedClassDraftRef.current,
      property,
      value,
    );
    cnDeltaMergedRef.current = mergeTailwindFromCssEdit(cnDeltaMergedRef.current, property, value);
  }, []);

  /**
   * Hiding and showing again (UI pass, issue 7). Hiding writes `hidden` over whatever display
   * the element had, keeping it; showing takes the `hidden` back off, so the element returns as
   * it was rather than as a `block` - which is what flattened a flex button into two lines.
   */
  const toggleHidden = useCallback(
    (hide: boolean) => {
      const sel = hotEditStore.selection.get();
      if (!sel) return;
      // The preview shows it at once: hidden elements are kept on the page, dimmed, so they can
      // still be clicked and brought back.
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId: sel.elementId,
        patch: { kind: 'style', property: 'display', value: hide ? 'none' : '' },
      });
      if (!hide) {
        postToIframe({
          type: 'IDEOZ_EDIT_APPLY',
          elementId: sel.elementId,
          patch: { kind: 'className', remove: ['hidden'] },
        });
      }
      queue((s) => {
        const existing = pendingEdits
          .get()
          .find((e) => e.kind === 'class' && e.elementId === s.elementId) as
          | Extract<PendingEdit, { kind: 'class' }>
          | undefined;
        const delta = new Set((existing?.classDelta ?? '').split(/\s+/).filter(Boolean));
        const remove = new Set((existing?.classRemove ?? '').split(/\s+/).filter(Boolean));
        // Only a `hidden` that is already in the prototype needs taking out of it. Undoing a
        // hide made in this session is just dropping the change again, not a change of its own.
        const inSource = (s.className ?? '').split(/\s+/).includes('hidden');
        if (hide) {
          delta.add('hidden');
          remove.delete('hidden');
        } else {
          delta.delete('hidden');
          if (inSource) remove.add('hidden');
        }
        if (delta.size === 0 && remove.size === 0) {
          pendingEdits.set(
            pendingEdits.get().filter((e) => !(e.kind === 'class' && e.elementId === s.elementId)),
          );
          return null;
        }
        return {
          kind: 'class',
          elementId: s.elementId,
          selection: s,
          classDelta: [...delta].join(' '),
          classRemove: [...remove].join(' ') || undefined,
          keepConflicting: true,
          classReplace: true,
          label: `${hide ? 'Hid' : 'Brought back'} ${elementLabel(s)}`,
        };
      });
      // What the founder is looking at has changed, so the control that did it has to change
      // too: it said "Hide" after hiding, and clicking it again did nothing they could see.
      hotEditStore.selection.set({ ...sel, isHidden: hide });
    },
    [postToIframe, queue],
  );

  const applyInstantCss = useCallback(
    (elementId: string, property: string, value: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'style', property, value },
      });
    },
    [postToIframe],
  );

  const applyInstantText = useCallback(
    (elementId: string, text: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'text', value: text },
      });
    },
    [postToIframe],
  );

  const scheduleApplyInstantText = useCallback(
    (elementId: string, text: string) => {
      pendingInstantTextRef.current = { elementId, text };
      if (textInstantRafRef.current != null) return;
      textInstantRafRef.current = window.requestAnimationFrame(() => {
        textInstantRafRef.current = null;
        const p = pendingInstantTextRef.current;
        if (!p) return;
        postToIframe({
          type: 'IDEOZ_EDIT_APPLY',
          elementId: p.elementId,
          patch: { kind: 'text', value: p.text },
        });
      });
    },
    [postToIframe],
  );

  /** A record's cell as the founder types it, drawn into the preview before Apply. */
  const applyInstantData = useCallback(
    (rowKey: string, oldValue: string, newValue: string, cellIndex = -1) => {
      postToIframe({ type: 'IDEOZ_EDIT_DATA_CELL', rowKey, oldValue, newValue, cellIndex });
    },
    [postToIframe],
  );

  /** A record added, copied, removed or moved, drawn into the preview before Apply. */
  const applyInstantRow = useCallback(
    (row: {
      op: 'add' | 'duplicate' | 'remove' | 'move';
      rowKey: string;
      newKey?: string;
      toIndex?: number;
      values?: { from: string; to: string }[];
    }) => {
      postToIframe({ type: 'IDEOZ_EDIT_DATA_ROW', ...row });
    },
    [postToIframe],
  );

  const applyInstantDom = useCallback(
    (elementId: string, key: string, value: string) => {
      postToIframe({
        type: 'IDEOZ_EDIT_APPLY',
        elementId,
        patch: { kind: 'dom', key, value },
      });
    },
    [postToIframe],
  );

  const selectElement = useCallback(
    (elementId: string) => postToIframe({ type: 'IDEOZ_EDIT_SELECT_ELEMENT', elementId }),
    [postToIframe],
  );
  const selectParent = useCallback(() => postToIframe({ type: 'IDEOZ_EDIT_SELECT_PARENT' }), [postToIframe]);
  const deselect = useCallback(() => postToIframe({ type: 'IDEOZ_EDIT_DESELECT' }), [postToIframe]);
  /** Puts the preview back for one element: drop its inline changes, then replay what is left. */
  const replayPreview = useCallback(
    (elementId: string, edits: readonly PendingEdit[]) => {
      postToIframe({ type: 'IDEOZ_EDIT_CLEAR_INLINE', elementId });
      for (const edit of edits) {
        if (edit.elementId !== elementId) continue;
        if (edit.kind === 'text') postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'text', value: edit.text } });
        if (edit.kind === 'attr')
          postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'dom', key: edit.attribute, value: edit.value } });
        if (edit.kind === 'image')
          postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'dom', key: 'src', value: edit.dataUri } });
      }
      // No picture change left for this element: the one it was drawn with comes back.
      if (!edits.some((e) => e.elementId === elementId && e.kind === 'image')) {
        const sel = hotEditStore.selection.get();
        if (sel?.elementId === elementId && sel.imageSrc) {
          postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'dom', key: 'src', value: sel.imageSrc } });
        }
      }
    },
    [postToIframe],
  );

  /**
   * Put the stylesheet's own colours back in the preview. A design system colour is shown by
   * overriding the variable on the document, so both Apply and Discard have to lift that
   * override: after Apply the file itself holds the colour, after Discard nothing does.
   */
  const clearThemePreview = useCallback(
    (edits: readonly PendingEdit[]) => {
      for (const edit of edits) {
        if (edit.kind === 'theme') postToIframe({ type: 'IDEOZ_EDIT_SET_TOKEN', token: edit.token, value: null });
      }
    },
    [postToIframe],
  );

  /** Apply: writes every pending change, then the preview recompiles once. */
  const applyPendingEdits = useCallback(async (): Promise<boolean> => {
    const edits = pendingEdits.get();
    if (edits.length === 0) return true;
    hotEditStore.save.set('saving');

    const result = await applyEdits({
      edits,
      wc: workbenchStore.webcontainer.get() as WebContainer | null,
      listScope: hotEditStore.listScope.get(),
    });

    if (!result.ok) {
      pendingEdits.set(result.remaining);
      lastSaveRef.current = async () => {
        await applyPendingEditsRef.current?.();
      };
      hotEditStore.save.set('failed');
      toastError(result.error);
      emitHotEditAnalytics('hot_edit_save_failed', { reason: result.error });
      return false;
    }

    // A design system change belongs to the startup, not to this prototype's stylesheet: the
    // app sends it on, so every prototype generated after this one is built with it.
    const designSystem = designSystemPatchFor(edits);
    if (designSystem) {
      window.dispatchEvent(new CustomEvent(HOT_EDIT_DESIGN_SYSTEM_EVENT, { detail: { patch: designSystem } }));
    }

    // The files now hold the changes; the inline copies would otherwise sit on top of them.
    clearThemePreview(edits);
    for (const elementId of touchedElements(edits)) {
      postToIframe({ type: 'IDEOZ_EDIT_CLEAR_INLINE', elementId });
    }
    pendingEdits.set([]);
    markSaved();
    // The recompile this causes is not the founder leaving the editor.
    holdEditModeThroughReload();
    emitHotEditAnalytics('hot_edit_patch_applied', { kind: 'apply', count: result.written });
    return true;
  }, [markSaved, postToIframe, clearThemePreview]);
  const applyPendingEditsRef = useRef<(() => Promise<boolean>) | null>(null);
  applyPendingEditsRef.current = applyPendingEdits;

  /** Discard: the preview goes back to the prototype as it is saved. */
  const discardPendingEdits = useCallback(() => {
    const edits = pendingEdits.get();
    clearThemePreview(edits);
    for (const elementId of touchedElements(edits)) {
      postToIframe({ type: 'IDEOZ_EDIT_CLEAR_INLINE', elementId });
      const sel = hotEditStore.selection.get();
      if (sel?.elementId === elementId) {
        postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'text', value: sel.textContent } });
        if (sel.imageSrc) postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId, patch: { kind: 'dom', key: 'src', value: sel.imageSrc } });
      }
    }
    pendingEdits.set([]);
    hotEditStore.save.set('idle');
  }, [postToIframe, clearThemePreview]);

  /** Undo before Apply walks the pending list; after Apply it walks the written history. */
  const undoPendingEdit = useCallback((): boolean => {
    const { list, dropped } = dropLast(pendingEdits.get());
    if (!dropped) return false;
    pendingEdits.set(list);
    clearThemePreview([dropped]);
    replayPreview(dropped.elementId, list);
    hotEditStore.save.set(list.length > 0 ? 'pending' : 'idle');
    return true;
  }, [replayPreview, clearThemePreview]);

  /**
   * A picture of the element, from the preview itself. Answers null rather than waiting forever
   * when the preview is busy or the element cannot be drawn: the chip shows its name instead.
   */
  const snapshotElement = useCallback(
    (elementId: string, timeoutMs = 2500): Promise<string | null> => {
      const requestId = `snap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      return new Promise<string | null>((resolve) => {
        const done = (dataUrl: string | null) => {
          window.clearTimeout(timer);
          resolve(dataUrl);
        };
        const timer = window.setTimeout(() => {
          snapshotWaitersRef.current.delete(requestId);
          resolve(null);
        }, timeoutMs);
        snapshotWaitersRef.current.set(requestId, done);
        postToIframe({ type: 'IDEOZ_EDIT_SNAPSHOT', requestId, elementId });
      });
    },
    [postToIframe],
  );

  /**
   * Ask Ideoz: the founder gets a chip of the element they picked and an empty composer to say
   * what they want in their own words. What the model needs to find the element travels with the
   * message instead of being pasted into it (`elementContext`).
   */
  const askIdeozAboutSelection = useCallback(
    (sel: HotEditSelection) => {
      const label = elementLabel(sel);
      // The picture takes a moment to draw, so the chip appears at once and gains it after.
      // Both events carry the same `askId`, which is how the chat knows it is the same chip.
      const askId = `ask-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const ask = {
        askId,
        label,
        context: elementContextOf(sel, { label, pathname: hotEditStore.iframePathname.get() }),
      };
      hotEditStore.pendingAsk.set(ask);
      // The chat opens and takes the founder's cursor; the chip itself reads the store.
      window.dispatchEvent(new CustomEvent(HOT_EDIT_ASK_IDEOZ_EVENT, { detail: { ...ask } }));
      void snapshotElement(sel.elementId).then((screenshot) => {
        if (!screenshot) return;
        const current = hotEditStore.pendingAsk.get();
        // The founder may have taken the chip off, or picked another element, while it drew.
        if (current?.askId === askId) hotEditStore.pendingAsk.set({ ...current, screenshot });
      });
    },
    [snapshotElement],
  );

  const retryLastSave = useCallback(() => {
    const retry = lastSaveRef.current;
    if (!retry) return;
    lastSaveRef.current = null;
    hotEditStore.save.set('saving');
    void retry();
  }, []);

  const selectByKey = useCallback(
    (key: string) => postToIframe({ type: 'IDEOZ_EDIT_SELECT_BY_KEY', key }),
    [postToIframe],
  );

  const exitHotEditMode = useCallback(() => {
    hotEditStore.pendingAsk.set(null);
    forgetTakenAsk();
    hotEditStore.mode.set(false);
    hotEditStore.selection.set(null);
    hotEditStore.dataGrid.set(null);
    mergedClassDraftRef.current = '';
    cnDeltaMergedRef.current = '';
    postToIframe({ type: 'IDEOZ_EDIT_ENABLE', enabled: false });
  }, [postToIframe]);

  /**
   * Resolves when the preview next checks in - or after `BRIDGE_HELLO_WAIT_MS`, so a preview
   * that never comes back leaves the founder with an editor rather than a dead button.
   */
  const waitForBridgeHello = useCallback(
    () =>
      new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        bridgeHelloWaitersRef.current.push(finish);
        window.setTimeout(finish, BRIDGE_HELLO_WAIT_MS);
      }),
    [],
  );

  const toggleHotEditMode = useCallback(async () => {
    const wc = workbenchStore.webcontainer.get();
    const next = !hotEditStore.mode.get();
    if (next) {
      // The light preview has no WebContainer: the bridge goes into the file store it compiles.
      // Every time, not once per session: a build seeds the project with the files Chef saved,
      // and those carry whatever bridge the prototype was built with. Memoising this left a
      // freshly built prototype running an old bridge - no design-system colours, no element
      // pictures, marks ignored - with nothing on screen to say why. `ensureHotEditBridgeFiles`
      // writes only when the content differs, so a repeat call costs three string compares.
      // Writing them compiles the prototype again and the preview reloads. Turning edit mode on
      // straight away handed the founder a page that was on its way out: their first click went
      // nowhere, and the preview blinked under them. So we wait for the new page to say hello.
      const rebuilding = await ensureHotEditBridgeFiles(wc);
      if (rebuilding) await waitForBridgeHello();
      emitHotEditAnalytics('hot_edit_enabled', { pathname: hotEditStore.iframePathname.get() });
      hotEditStore.mode.set(true);
      return;
    }
    // Leaving with changes that were never applied asks first.
    requestLeaveEditMode(() => {
      exitHotEditMode();
      emitHotEditAnalytics('hot_edit_disabled', {});
    });
  }, [exitHotEditMode, waitForBridgeHello]);

  /** Show a design system colour across the whole preview, before Apply writes the stylesheet. */
  const previewThemeToken = useCallback(
    (token: string, value: string | null) => {
      postToIframe({ type: 'IDEOZ_EDIT_SET_TOKEN', token, value });
    },
    [postToIframe],
  );

  /**
   * Queue the design system colour itself. The element keeps its `bg-primary`, so nothing is
   * written on it: what changes is what Primary means.
   */
  const queueThemeToken = useCallback((token: string, value: string, label: string) => {
    const sel = hotEditStore.selection.get();
    if (!sel) return;
    pendingEdits.set(
      queueEdit(pendingEdits.get(), { kind: 'theme', elementId: sel.elementId, selection: sel, token, value, label }),
    );
    hotEditStore.save.set('pending');
  }, []);

  /**
   * Another picture in the selected <img> (Prototype Editor UX plan, step 6). The bytes go into
   * the store now - an upload has nowhere else to live, and the library's are already there - the
   * preview shows them at once, and the reference is written by Apply like any other change.
   */
  const replaceImage = useCallback(
    (args: { storePath: string; reference: string; dataUri: string }) => {
      const sel = hotEditStore.selection.get();
      if (!sel) return;
      const parsed = parseDataUri(args.dataUri);
      if (parsed && !workbenchStore.assets.get()[args.storePath]) {
        workbenchStore.assets.setKey(args.storePath, { base64: parsed.base64, mimeType: parsed.mimeType });
      }
      postToIframe({ type: 'IDEOZ_EDIT_APPLY', elementId: sel.elementId, patch: { kind: 'dom', key: 'src', value: args.dataUri } });
      const fromStorePath = assetPathForDataUri(workbenchStore.assets.get(), sel.imageSrc);
      queue(() => ({
        kind: 'image',
        elementId: sel.elementId,
        selection: sel,
        fromStorePath,
        storePath: args.storePath,
        reference: args.reference,
        dataUri: args.dataUri,
        label: `Picture of ${elementLabel(sel)}`,
      }));
    },
    [postToIframe, queue],
  );

  /**
   * Another icon in place of the selected one. There is nothing to show in the preview until the
   * file is written - the icon is a component, not a class - so the founder sees it on Apply.
   */
  const swapIcon = useCallback(
    (iconName: string, markup?: string) => {
      const sel = hotEditStore.selection.get();
      // Shown before it is saved, like every other change: the icon the founder picked is drawn
      // into the preview now, and the change joins the others waiting for Apply.
      if (sel && markup) {
        postToIframe({
          type: 'IDEOZ_EDIT_APPLY',
          elementId: sel.elementId,
          patch: { kind: 'icon', name: kebabIconName(iconName), markup },
        });
      }
      queue((sel2) => ({
        kind: 'icon',
        elementId: sel2.elementId,
        selection: sel2,
        iconName,
        label: `Icon of ${elementLabel(sel2)}`,
      }));
    },
    [postToIframe, queue],
  );

  /** Ask Ideoz about whatever is selected now (the toolbar's button, the panel's link). */
  const askIdeoz = useCallback(() => {
    const sel = hotEditStore.selection.get();
    if (sel) askIdeozAboutSelection(sel);
  }, [askIdeozAboutSelection]);

  const apiStable = useRef<HotEditBridgeApi | null>(null);
  apiStable.current = {
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    applyInstantData,
    applyInstantRow,
    bumpMergedFromTailwind,
    toggleHidden,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    openSelectionInEditor,
    exitHotEditMode,
    selectElement,
    selectParent,
    deselect,
    selectByKey,
    retryLastSave,
    applyPendingEdits,
    discardPendingEdits,
    undoPendingEdit,
    previewThemeToken,
    queueThemeToken,
    askIdeoz,
    replaceImage,
    swapIcon,
  };

  useEffect(() => {
    const api = apiStable.current!;
    hotEditStore.bridgeApi.set(api);
    return () => hotEditStore.bridgeApi.set(null);
  }, [
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    applyInstantData,
    applyInstantRow,
    bumpMergedFromTailwind,
    toggleHidden,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    openSelectionInEditor,
    exitHotEditMode,
    selectElement,
    selectParent,
    deselect,
    selectByKey,
    retryLastSave,
    applyPendingEdits,
    discardPendingEdits,
    undoPendingEdit,
    previewThemeToken,
    queueThemeToken,
    askIdeoz,
    replaceImage,
    swapIcon,
  ]);

  useEffect(() => {
    return () => {
      clearPersistTimers();
      if (textInstantRafRef.current != null) window.cancelAnimationFrame(textInstantRafRef.current);
    };
  }, [clearPersistTimers]);

  return {
    previewIframeRef,
    bridgeReady,
    toggleHotEditMode,
    applyInstantCss,
    applyInstantText,
    scheduleApplyInstantText,
    applyInstantDom,
    applyInstantData,
    applyInstantRow,
    queuePersistTypographyLayout,
    queuePersistText,
    queuePersistPlaceholder,
    queuePersistDomValue,
    queuePersistSelectOptions,
    queuePersistInputType,
    bumpMergedFromTailwind,
    toggleHidden,
    mergedClassDraftRef,
    cnDeltaMergedRef,
    exitHotEditMode,
    openSelectionInEditor,
  };
}
